import { fetch_requestor_application_journals } from '@/services/requestor_portal_service';
import { to_array, to_number } from '../CompanyCases/company_cases_common';
import {
  application_case_code,
  clean_task,
  first_clean_value,
  first_value,
  grouped_rows,
  is_billable,
  journal_hours,
  profile_values,
  sum_field,
  user_name,
} from './company_reports_billable_utils';

const CASE_TITLE_FIELDS = ['title', 'entity_profile_name', 'name', 'description'];
const CASE_DOMAIN_FIELDS = ['domain', 'domain_name', 'entity_type_name', 'entity_type_label'];

export async function load_billable_report_rows(profiles = []) {
  const responses = await Promise.allSettled(
    profiles.map((profile) => billable_rows_for_profile(profile))
  );

  return responses
    .filter((response) => response.status === 'fulfilled')
    .flatMap((response) => response.value);
}

export function billable_report_description(rows = []) {
  const summary = billable_summary(rows);
  return `${summary.billable_hours} billable hour${plural(summary.billable_hours)} across ${summary.active_cases} active case${plural(summary.active_cases)}`;
}

export function billable_report_sections(rows = []) {
  const summary = billable_summary(rows);
  return [
    executive_summary_section(summary),
    timekeeper_summary_section(rows, summary),
    case_summary_section(rows, summary),
    detailed_entries_section(rows),
  ];
}

export function billable_summary_rows(rows = []) {
  return billable_report_sections(rows).flatMap((section) => section.rows || []);
}

async function billable_rows_for_profile(profile = {}) {
  const application_id = to_number(profile.application_id || profile.latest_application_id);
  if (!application_id) return [];

  const journals = await fetch_requestor_application_journals(application_id);
  return to_array(journals)
    .filter((journal) => journal_hours(journal) > 0)
    .map((journal) => billable_entry_row(journal, profile));
}

function billable_entry_row(journal = {}, profile = {}) {
  return {
    section: 'Detailed Time Entries',
    matter_id: billable_case_label(profile),
    client_case_name: billable_case_title(profile),
    practice_area: billable_case_domain(profile),
    date: journal.journal_date || journal.date_created || '',
    timekeeper: user_name(journal),
    title: journal.role || journal.role_name || journal.user_role || 'Not returned',
    hourly_rate: journal.hourly_rate || journal.rate || '-',
    billable_hours: is_billable(journal) ? journal_hours(journal) : 0,
    non_billable_hours: is_billable(journal) ? 0 : journal_hours(journal),
    total_value: journal.amount || journal.total_amount || journal.invoice_amount || '-',
    task: clean_task(journal.journal_description),
  };
}

function billable_case_label(profile = {}) {
  return first_value([
    profile.case_code,
    profile.application_code,
    profile.reference,
    application_case_code(profile),
  ]);
}

function billable_case_title(profile = {}) {
  return first_clean_value(profile_values(profile, CASE_TITLE_FIELDS), 'Untitled case');
}

function billable_case_domain(profile = {}) {
  return first_clean_value(profile_values(profile, CASE_DOMAIN_FIELDS), 'Not returned');
}

function timekeeper_rows(rows = []) {
  return grouped_rows(rows, (row) => row.timekeeper).map((group) => ({
    section: 'Summary by Timekeeper',
    timekeeper: group.key,
    title: first_value(group.rows.map((row) => row.title)),
    hourly_rate: first_value(group.rows.map((row) => row.hourly_rate)),
    billable_hours: sum_field(group.rows, 'billable_hours'),
    total_value: first_value(group.rows.map((row) => row.total_value)),
  }));
}

function case_rows(rows = []) {
  return grouped_rows(rows, (row) => row.matter_id).map((group) => ({
    section: 'Summary by Case / Matter',
    matter_id: group.key,
    client_case_name: first_value(group.rows.map((row) => row.client_case_name)),
    practice_area: first_value(group.rows.map((row) => row.practice_area)),
    total_hours: sum_field(group.rows, 'billable_hours'),
    total_value: first_value(group.rows.map((row) => row.total_value)),
  }));
}

function executive_summary_section(summary = {}) {
  return {
    title: '1. Executive Summary',
    description: 'This section provides a high-level overview of team performance, total time logged, and the financial value of the billable work completed.',
    type: 'metrics',
    rows: [
      { metric: 'Total Active Cases', value: summary.active_cases },
      { metric: 'Total Timekeepers', value: summary.timekeepers },
      { metric: 'Total Billable Hours', value: summary.billable_hours },
      { metric: 'Total Non-Billable Hours', value: summary.non_billable_hours },
      { metric: 'Total Billed Value', value: summary.total_value },
      { metric: 'Overall Team Utilization Rate', value: `${summary.utilization_rate}%` },
    ],
  };
}

function timekeeper_summary_section(rows = [], summary = {}) {
  return {
    title: '2. Summary by Timekeeper',
    description: 'A breakdown of how much time and value each professional contributed during the selected billing cycle.',
    type: 'table',
    columns: [
      { key: 'timekeeper', label: 'Timekeeper' },
      { key: 'title', label: 'Title' },
      { key: 'hourly_rate', label: 'Hourly Rate' },
      { key: 'billable_hours', label: 'Billable Hours' },
      { key: 'total_value', label: 'Total Value' },
    ],
    rows: [...timekeeper_rows(rows), timekeeper_total_row(summary)],
  };
}

function case_summary_section(rows = [], summary = {}) {
  return {
    title: '3. Summary by Case / Matter',
    description: 'A breakdown of time and resources allocated to specific client accounts or cases.',
    type: 'table',
    columns: [
      { key: 'matter_id', label: 'Matter ID' },
      { key: 'client_case_name', label: 'Client / Case Name' },
      { key: 'practice_area', label: 'Practice Area' },
      { key: 'total_hours', label: 'Total Hours' },
      { key: 'total_value', label: 'Total Billed Value' },
    ],
    rows: [...case_rows(rows), case_total_row(summary)],
  };
}

function detailed_entries_section(rows = []) {
  return {
    title: '4. Detailed Time Entries',
    description: 'Detailed chronological line items grouped by matter.',
    type: 'grouped_entries',
    groups: grouped_rows(rows, (row) => row.matter_id).map((group) => ({
      title: `Matter: ${group.rows[0]?.matter_id || 'Unassigned'} | ${group.rows[0]?.client_case_name || 'Untitled case'}`,
      rows: group.rows,
    })),
  };
}

function timekeeper_total_row(summary = {}) {
  return { timekeeper: 'TOTAL', title: '', hourly_rate: '', billable_hours: summary.billable_hours, total_value: summary.total_value };
}

function case_total_row(summary = {}) {
  return { matter_id: 'TOTAL', client_case_name: '', practice_area: '', total_hours: summary.billable_hours, total_value: summary.total_value };
}

function billable_summary(rows = []) {
  const billable_hours = sum_field(rows, 'billable_hours');
  const non_billable_hours = sum_field(rows, 'non_billable_hours');
  const total_hours = billable_hours + non_billable_hours;

  return {
    active_cases: new Set(rows.map((row) => row.matter_id).filter(Boolean)).size,
    timekeepers: new Set(rows.map((row) => row.timekeeper).filter(Boolean)).size,
    billable_hours,
    non_billable_hours,
    total_value: first_value(rows.map((row) => row.total_value)),
    utilization_rate: total_hours ? Math.round((billable_hours / total_hours) * 100) : 0,
  };
}

function plural(count) {
  return Number(count) === 1 ? '' : 's';
}
